import React from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';

const CORE_METRICS = ['LCP', 'INP', 'CLS'];
const OTHER_METRICS = ['FCP', 'TTFB', 'RTT', 'FID'];

export const StatusSummary = ({ type = 'core', overallStatus = 'good', selectedDevice = 'ALL', deviceStatuses = {} }) => {
  const { t } = useLanguage();
  
  const deviceNames = {
    'ALL': t('statusSummary.devices.all'),
    'PHONE': t('statusSummary.devices.phone'),
    'DESKTOP': t('statusSummary.devices.desktop'),
    'TABLET': t('statusSummary.devices.tablet')
  };
  
  const isAllDevices = selectedDevice === 'ALL';
  
  const getAllProblematicDevices = () => {
    const problems = [];
    Object.entries(deviceStatuses).forEach(([device, status]) => {
      if (device !== 'ALL' && (status === 'poor' || status === 'needs-improvement')) {
        problems.push({ device, status, name: deviceNames[device] || device });
      }
    });
    return problems;
  };
  
  const getOtherProblematicDevices = () => {
    const problems = [];
    Object.entries(deviceStatuses).forEach(([device, status]) => {
      if (device !== selectedDevice && device !== 'ALL' && (status === 'poor' || status === 'needs-improvement')) {
        problems.push({ device, status, name: deviceNames[device] || device });
      }
    });
    return problems;
  };
  
  const allProblematicDevices = getAllProblematicDevices();
  const otherProblematicDevices = getOtherProblematicDevices();
  
  const hasAnyDeviceProblems = allProblematicDevices.length > 0;
  const hasOtherDeviceProblems = otherProblematicDevices.length > 0;
  
  const getMainText = () => {
    if (type === 'core') {
      if (overallStatus === 'poor') return t('statusSummary.core.criticalProblems');
      if (overallStatus === 'needs-improvement') return t('statusSummary.core.needsImprovement');
      if (isAllDevices) {
        return t('statusSummary.overallOk');
      }
      return t('statusSummary.deviceOk', { device: deviceNames[selectedDevice] });
    } else {
      if (overallStatus === 'poor') return t('statusSummary.other.needsAttention');
      if (overallStatus === 'needs-improvement') return t('statusSummary.other.canBeImproved');
      if (isAllDevices) {
        return t('statusSummary.overallOk');
      }
      return t('statusSummary.deviceOk', { device: deviceNames[selectedDevice] });
    }
  };
  
  const getSecondaryText = () => {
    if (isAllDevices) {
      if (hasAnyDeviceProblems) {
        const poorDevices = allProblematicDevices.filter(d => d.status === 'poor');
        const fairDevices = allProblematicDevices.filter(d => d.status === 'needs-improvement');
        
        if (poorDevices.length > 0) {
          return `${t('statusSummary.warning')}: ${poorDevices.map(d => d.name).join(', ')} – ${t('statusSummary.critical')}`;
        }
        if (fairDevices.length > 0) {
          return `${t('statusSummary.tip')}: ${fairDevices.map(d => d.name).join(', ')} – ${t('statusSummary.toImprove')}`;
        }
      } else {
        return t('statusSummary.allDevicesOk');
      }
    }
    if (!isAllDevices && overallStatus === 'good' && hasOtherDeviceProblems) {
      const poorDevices = otherProblematicDevices.filter(d => d.status === 'poor');
      const fairDevices = otherProblematicDevices.filter(d => d.status === 'needs-improvement');
      
      if (poorDevices.length > 0) {
        return `${t('statusSummary.warning')}: ${poorDevices.map(d => d.name).join(', ')} ${t('statusSummary.hasProblems')}`;
      }
      if (fairDevices.length > 0) {
        return `${t('statusSummary.tip')}: ${fairDevices.map(d => d.name).join(', ')} ${t('statusSummary.canBeImproved')}`;
      }
    }
    return null;
  };
  
  const getStyle = () => {
    if (overallStatus === 'poor') return 'text-red-400 bg-red-500/10 border-red-500/30';
    if (overallStatus === 'needs-improvement') return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
    if (hasOtherDeviceProblems) return 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30';
    return 'text-green-400 bg-green-500/10 border-green-500/30';
  };
  
  const getIcon = () => {
    if (overallStatus === 'poor') return <Icons.Error size={14} />;
    if (overallStatus === 'needs-improvement') return <Icons.Warning size={14} />;
    if (hasOtherDeviceProblems) return <Icons.Info size={14} />;
    return <Icons.Check size={14} />;
  };
  
  const secondaryText = getSecondaryText();

  const getSecondaryStyle = () => {
    if (isAllDevices && hasAnyDeviceProblems) {
      const poorDevices = allProblematicDevices.filter(d => d.status === 'poor');
      if (poorDevices.length > 0) return 'text-red-400';
      return 'text-amber-400';
    }
    if (hasOtherDeviceProblems) {
      const poorDevices = otherProblematicDevices.filter(d => d.status === 'poor');
      if (poorDevices.length > 0) return 'text-red-400';
      return 'text-amber-400';
    }
    return 'text-slate-400';
  };

  return (
    <div className={`inline-flex flex-wrap items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-full border text-xs font-medium min-h-[28px] sm:min-h-[32px] max-w-full ${getStyle()}`}>
      <span className="flex-shrink-0">{getIcon()}</span>
      <span className="break-words">{getMainText()}</span>
      {secondaryText && (
        <span className={`${getSecondaryStyle()} break-words`}>• {secondaryText}</span>
      )}
    </div>
  );
};

const SectionInfoPanel = ({ type = 'core', isOpen, onClose }) => {
  const { t } = useLanguage();
  const metrics = type === 'core' ? CORE_METRICS : OTHER_METRICS;
  const infoKey = type === 'core' ? 'sectionInfo.coreVitals' : 'sectionInfo.otherMetrics';

  if (!isOpen) return null;

  return (
    <div className="mb-6 p-4 rounded-xl bg-slate-800/80 border border-slate-600/50 animate-slide-in">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Icons.Info size={18} className="text-cyan-400" />
            {t(`${infoKey}.title`)}
          </h3>
          <p className="text-sm text-slate-400 mt-1">{t(`${infoKey}.description`)}</p>
        </div>
        <button 
          onClick={onClose}
          className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
        >
          <Icons.Close size={18} />
        </button>
      </div>
      
      <div className="space-y-3">
        {metrics.map((metricKey) => {
          const metric = t(`${infoKey}.metrics.${metricKey.toLowerCase()}`);
          if (!metric || typeof metric !== 'object') return null;
          
          return (
            <div key={metricKey} className="p-3 rounded-lg bg-slate-900/50 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-white">{metricKey}</span>
                <span className="text-xs text-slate-500">({metric.fullName})</span>
              </div>
              <p className="text-sm text-slate-300 mb-2">{metric.description}</p>
              <div className="flex flex-wrap gap-2 text-xs mb-2">
                <span className="px-2 py-0.5 rounded bg-green-500/20 text-green-400">GOOD: {metric.good}</span>
                <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">FAIR: {metric.fair}</span>
                <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-400">POOR: {metric.poor}</span>
              </div>
              <p className="text-xs text-cyan-400 flex items-start gap-1">
                <Icons.Lightning size={12} className="mt-0.5 flex-shrink-0" />
                {metric.tip}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SectionInfoPanel;
